// service/Talleres.js
import instance from "../apis/app";
import { convertirImagenAWebp } from "../libs/optimizarImagen";
import { errorDeApi } from "./client";


const Post = async (data) => {
    try {
        const response = await instance.post("/taller", data);
        return response;
    } catch (error) {
        throw errorDeApi(error);
    }
}


const Update = async (id, data) => {
    try {
        const response = await instance.put(`/taller/${id}`, data);
        return response;
    } catch (error) {
        throw errorDeApi(error);
    }
}


const getById = async (id) => {
    try {
        const response = await instance.get(`/taller/${id}`);
        return response.data;
    } catch (error) {
        throw errorDeApi(error);
    }
}


// ========================================
// 🚀 MÉTODO LIST ACTUALIZADO CON PAGINACIÓN Y FILTROS
// ========================================
/**
 * Lista talleres con paginación y filtros del lado del servidor
 * @param {Object} params - Parámetros de búsqueda
 * @param {number} params.page - Página actual (default: 1)
 * @param {number} params.limit - Items por página (default: 10)
 * @param {string} params.search - Búsqueda por texto (nombre, descripción, tipo)
 * @param {boolean} params.allTalleres - Si true, trae todos los talleres sin filtro de estado
 * @param {string} params.recinto - ID del recinto para filtrar
 * @param {string} params.tipoActividad - Tipo de actividad para filtrar
 * @param {string} params.tipoPrecio - 'gratuitos', 'pago' o 'todos'
 * @param {number} params.edadMin - Edad mínima para filtrar
 * @param {number} params.edadMax - Edad máxima para filtrar
 * @returns {Promise<object>} Objeto con talleres, total, page, limit, totalPages, stats
 */
const List = async (params = {}) => {
    try {
        const queryParams = new URLSearchParams();
        
        // Parámetros de paginación
        if (params.page) queryParams.append('page', params.page);
        if (params.limit) queryParams.append('limit', params.limit);
        
        // Búsqueda por texto
        if (params.search) queryParams.append('search', params.search);
        
        // Filtro para traer todos los talleres (sin filtro de estado)
        if (params.allTalleres) queryParams.append('allTalleres', 'true');
        
        // 🆕 Filtros adicionales
        if (params.recinto) queryParams.append('recinto', params.recinto);
        if (params.tipoActividad) queryParams.append('tipoActividad', params.tipoActividad);
        if (params.tipoPrecio && params.tipoPrecio !== 'todos') {
            queryParams.append('tipoPrecio', params.tipoPrecio);
        }
        if (params.edadMin) queryParams.append('edadMin', params.edadMin);
        if (params.edadMax) queryParams.append('edadMax', params.edadMax);
        
        const queryString = queryParams.toString();
        const url = `/talleres${queryString ? `?${queryString}` : ''}`;
        
        const response = await instance.get(url);
        return response;
    } catch (error) {
        throw errorDeApi(error);
    }
};


const Activate = async (id) => {
    try {
        const response = await instance.patch(`/taller/${id}/reanudar`);
        return response;
    } catch (error) {
        throw errorDeApi(error);
    }
};


const Pause = async (id) => {
    try {
        const response = await instance.patch(`/taller/${id}/pausar`);
        return response;
    } catch (error) {
        throw errorDeApi(error);
    }
};


const ToggleActivo = async (id, { estado }) => {
    if (estado === "activo") {
        return await Activate(id);
    } else {
        return await Pause(id);
    }
}


const OpenInscripciones = async (id) => {
    try {
        const response = await instance.patch(`/taller/${id}/inscripciones/abrir`);
        return response;
    } catch (error) {
        throw errorDeApi(error);
    }
};


const CloseInscripciones = async (id) => {
    try {
        const response = await instance.patch(`/taller/${id}/inscripciones/cerrar`);
        return response;
    } catch (error) {
        throw errorDeApi(error);
    }
};


const ToggleInscripciones = async (id, { inscripcionesAbiertas }) => {
    const response = inscripcionesAbiertas == 'abiertas' ? OpenInscripciones(id) : CloseInscripciones(id);
    return response;
}


const Delete = async (id) => {
    try {
        const response = await instance.delete(`/taller/${id}`);
        return response;
    } catch (error) {
        throw errorDeApi(error);
    }
}


// ========================================
// OPERACIONES MASIVAS
// ========================================


/**
 * Actualiza el estado de múltiples talleres (activar o pausar)
 * @param {string[]} tallerIds - Array de IDs de talleres
 * @param {string} action - 'activate' o 'pause'
 * @returns {Promise<object>} Respuesta con resultados de éxito y errores
 */
const BulkUpdateStatus = async (tallerIds, action) => {
    try {
        const response = await instance.patch('/talleres/bulk-status', {
            tallerIds,
            action
        });
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};


/**
 * Activa múltiples talleres seleccionados
 * @param {string[]} tallerIds - Array de IDs de talleres
 * @returns {Promise<object>} Respuesta con resultados
 */
const BulkActivate = async (tallerIds) => {
    return await BulkUpdateStatus(tallerIds, 'activate');
};


/**
 * Pausa múltiples talleres seleccionados
 * @param {string[]} tallerIds - Array de IDs de talleres
 * @returns {Promise<object>} Respuesta con resultados
 */
const BulkPause = async (tallerIds) => {
    return await BulkUpdateStatus(tallerIds, 'pause');
};


/**
 * 🆕 Actualiza las fechas de actividades de múltiples talleres
 * @param {string[]} tallerIds - Array de IDs de talleres
 * @param {string} actividadesInicio - Fecha de inicio en formato ISO (ej: '2026-03-01T08:00:00.000Z')
 * @param {string} actividadesFin - Fecha de fin en formato ISO (ej: '2026-06-30T20:00:00.000Z')
 * @returns {Promise<object>} Respuesta con resultados de éxito y errores
 */
const BulkUpdateActivityDates = async (tallerIds, actividadesInicio, actividadesFin) => {
    try {
        const response = await instance.patch('/talleres/bulk-activity-dates', {
            tallerIds,
            actividadesInicio,
            actividadesFin
        });
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};


// ========================================
// SISTEMA DE APROBACIONES
// ========================================


const ListarCambiosPendientes = async () => {
    try {
        const response = await instance.get(`/talleres/cambios-pendientes`);
        return response.data;
    } catch (error) {
        throw errorDeApi(error);
    }
}


const AprobarCambio = async (id) => {
    try {
        const response = await instance.post(`/taller/cambio/${id}/aprobar`);
        return response.data;
    } catch (error) {
        throw errorDeApi(error);
    }
}


const RechazarCambio = async (id, motivoRechazo) => {
    try {
        const response = await instance.post(`/taller/cambio/${id}/rechazar`, {
            motivoRechazo
        });
        return response.data;
    } catch (error) {
        throw errorDeApi(error);
    }
}


const ObtenerHistorial = async (tallerId) => {
    try {
        const response = await instance.get(`/taller/${tallerId}/historial`);
        return response.data;
    } catch (error) {
        throw errorDeApi(error);
    }
}


const RestaurarVersion = async (tallerId, historialId) => {
    try {
        const response = await instance.post(`/taller/${tallerId}/restaurar/${historialId}`);
        return response.data;
    } catch (error) {
        throw errorDeApi(error);
    }
}


// ========================================
// MÉTODOS PARA INSCRIPCIONES
// ========================================


const GetTalleresDisponibles = async (filtros = {}) => {
    try {
        const params = new URLSearchParams();
        
        if (filtros.edadMin) params.append('edadMin', filtros.edadMin);
        if (filtros.edadMax) params.append('edadMax', filtros.edadMax);
        if (filtros.tipoActividad) params.append('tipoActividad', filtros.tipoActividad);
        if (filtros.recinto) params.append('recinto', filtros.recinto);
        
        const response = await instance.get(`/talleres-inscripcion/disponibles?${params.toString()}`);
        return response.data;
    } catch (error) {
        throw errorDeApi(error);
    }
};


const VerificarDisponibilidad = async (tallerId) => {
    try {
        const response = await instance.get(`/taller/${tallerId}/disponibilidad`);
        return response.data;
    } catch (error) {
        throw errorDeApi(error);
    }
};


// ========================================
// 🆕 GESTIÓN DE ACADEMIAS - USUARIOS AUTORIZADOS
// ========================================


/**
 * Verifica si el usuario actual tiene acceso a un taller específico
 * @param {string} tallerId - ID del taller
 * @returns {Promise<object>} Respuesta con información de acceso
 */
const VerificarAccesoUsuario = async (tallerId) => {
    try {
        const response = await instance.get(`/taller/${tallerId}/verificar-acceso`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};


/**
 * Agrega usuarios autorizados a una academia
 * @param {string} tallerId - ID del taller (debe ser tipo 'academia')
 * @param {Array<object>} usuarios - Array de usuarios con estructura: 
 *   [{ cognitoId: string, email?: string, rut?: string, nombre?: string }]
 * @returns {Promise<object>} Respuesta con usuarios agregados y total
 */
const AgregarUsuariosAutorizados = async (tallerId, usuarios) => {
    try {
        const response = await instance.post(`/taller/${tallerId}/usuarios-autorizados`, {
            usuarios
        });
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};


/**
 * Elimina un usuario autorizado de una academia
 * @param {string} tallerId - ID del taller
 * @param {string} cognitoId - Cognito ID del usuario a eliminar
 * @returns {Promise<object>} Respuesta con confirmación
 */
const EliminarUsuarioAutorizado = async (tallerId, cognitoId) => {
    try {
        const response = await instance.delete(`/taller/${tallerId}/usuarios-autorizados/${cognitoId}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};


// ========================================
// MÉTODOS PARA MANEJO DE IMÁGENES
// ========================================


/**
 * Sube una imagen para un taller
 * @param {string} tallerId - ID del taller
 * @param {File} file - Archivo de imagen
 * @returns {Promise<object>} Respuesta con datos del taller actualizado
 */
const UploadImage = async (tallerId, file) => {
    try {
        // Se recomprime a WebP antes de subir: lo que llegue a S3 es lo que después
        // se le sirve a cada visitante, y ahí está el costo del bucket.
        const imagen = await convertirImagenAWebp(file);

        const formData = new FormData();
        formData.append('imagen', imagen);


        // IMPORTANTE: NO especificar Content-Type, dejar que Axios lo haga automáticamente
        const response = await instance.post(
            `/taller/${tallerId}/imagen`,
            formData
            // NO incluir headers aquí, dejar que sea automático
        );
        return response;
    } catch (error) {
        throw error.response?.data || error;
    }
};


/**
 * Elimina la imagen de un taller
 * @param {string} tallerId - ID del taller
 * @returns {Promise<object>} Respuesta con datos del taller actualizado
 */
const DeleteImage = async (tallerId) => {
    try {
        const response = await instance.delete(`/taller/${tallerId}/imagen`);
        return response;
    } catch (error) {
        throw errorDeApi(error);
    }
};


export default {
    Post,
    Update,
    getById,
    List,
    Activate,
    Pause,
    ToggleActivo,
    OpenInscripciones,
    CloseInscripciones,
    ToggleInscripciones,
    Delete,
    // Operaciones masivas
    BulkUpdateStatus,
    BulkActivate,
    BulkPause,
    BulkUpdateActivityDates, // 🆕 NUEVO
    // Sistema de aprobaciones
    ListarCambiosPendientes,
    AprobarCambio,
    RechazarCambio,
    ObtenerHistorial,
    RestaurarVersion,
    // Inscripciones
    GetTalleresDisponibles,
    VerificarDisponibilidad,
    // 🆕 Gestión de academias
    VerificarAccesoUsuario,
    AgregarUsuariosAutorizados,
    EliminarUsuarioAutorizado,
    // Imágenes
    UploadImage,
    DeleteImage,
};
